import { useState } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Clock, MessageCircle } from "lucide-react";

const CONTACT_PHONE = "+90 553 730 44 39";
const WHATSAPP_URL = "https://wa.me/905537304439";

export default function Contact() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajın bize ulaştı, en kısa sürede döneceğiz.");
      setForm({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="reveal">
          <span className="text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold">İletişim</span>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-semibold tracking-tighter" data-testid="contact-title">Bize ulaşın</h1>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl">
            Sorularınız, önerileriniz ya da işbirliği talepleriniz için aşağıdaki iletişim bilgilerinden ya da formdan bize yazın.
          </p>
        </div>

        {/* Contact info cards */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2 reveal reveal-delay-1">
          <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="contact-phone-card">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] grid place-items-center">
                <Phone size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-slate-500 font-medium">Telefon</div>
                <a href={`tel:${CONTACT_PHONE.replace(/\s/g, "")}`} className="font-display text-lg font-semibold text-slate-900 hover:text-[color:var(--brand)] transition-colors" data-testid="contact-phone-number">
                  {CONTACT_PHONE}
                </a>
              </div>
            </div>
            <a
              href={WHATSAPP_URL}
              target="_blank"
              rel="noreferrer"
              data-testid="whatsapp-btn"
              className="mt-5 inline-flex items-center gap-2 w-full justify-center bg-[#25D366] hover:bg-[#20BF5B] text-white rounded-full h-11 px-5 text-sm font-semibold active:scale-95 transition-transform shadow-sm shadow-green-500/20"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                <path d="M20.52 3.48A11.94 11.94 0 0012.04 0C5.5 0 .2 5.3.2 11.85c0 2.09.55 4.13 1.6 5.93L0 24l6.38-1.67a11.85 11.85 0 005.66 1.44h.01c6.54 0 11.85-5.3 11.85-11.85 0-3.17-1.24-6.15-3.38-8.44zM12.05 21.5h-.01a9.66 9.66 0 01-4.93-1.35l-.35-.21-3.79.99 1.01-3.69-.23-.38a9.62 9.62 0 01-1.49-5.16c0-5.32 4.33-9.65 9.7-9.65 2.59 0 5.02 1.01 6.85 2.84a9.6 9.6 0 012.83 6.83c0 5.36-4.33 9.68-9.62 9.68zm5.31-7.24c-.29-.15-1.72-.85-1.99-.95-.27-.1-.47-.15-.66.15-.2.29-.76.95-.93 1.14-.17.2-.34.22-.63.07-.29-.15-1.23-.45-2.35-1.45-.87-.77-1.45-1.72-1.62-2.01-.17-.29-.02-.45.13-.6.13-.13.29-.34.43-.51.15-.17.2-.29.29-.48.1-.2.05-.37-.02-.51-.07-.15-.66-1.58-.9-2.17-.24-.58-.49-.5-.66-.51h-.56c-.2 0-.51.07-.78.37-.27.29-1.02 1-1.02 2.44 0 1.44 1.05 2.83 1.19 3.03.15.2 2.07 3.16 5.02 4.43.7.3 1.25.48 1.68.62.7.22 1.34.19 1.85.11.56-.08 1.72-.7 1.97-1.38.24-.68.24-1.26.17-1.38-.07-.12-.27-.2-.56-.34z"/>
              </svg>
              WhatsApp ile Mesaj Gönder
            </a>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-[color:var(--brand-soft)] p-6" data-testid="contact-response-card">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-[color:var(--brand)] text-white grid place-items-center">
                <Clock size={20} />
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-[color:var(--brand)] font-semibold">Yanıt Süresi</div>
                <div className="font-display text-lg font-semibold text-slate-900">24 saat içinde</div>
              </div>
            </div>
            <p className="mt-5 text-sm text-slate-700 leading-relaxed" data-testid="response-notice">
              <strong className="text-slate-900">Aramanıza veya mesajınıza 24 saat içinde dönülecektir.</strong>{" "}
              Hafta sonları ve resmi tatillerde yanıt süresi bir miktar uzayabilir.
            </p>
          </div>
        </div>

        {/* Form */}
        <div className="mt-14 reveal reveal-delay-2">
          <div className="flex items-center gap-2 mb-4">
            <MessageCircle size={18} className="text-[color:var(--brand)]" />
            <h2 className="font-display text-2xl font-semibold">Bize yazın</h2>
          </div>
          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Ad Soyad</Label>
              <Input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 h-12" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Telefon</Label>
                <Input required data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2 h-12" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">E-posta</Label>
                <Input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 h-12" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-500 font-medium">Mesajınız</Label>
              <Textarea required rows={6} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 rounded-lg border-slate-300 focus:border-[color:var(--brand)]" />
            </div>
            <Button type="submit" disabled={loading} data-testid="contact-submit"
              className="w-full sm:w-auto h-12 px-8 rounded-full bg-[color:var(--brand)] hover:bg-[color:var(--brand-hover)] text-white">
              {loading ? "Gönderiliyor..." : "Mesaj Gönder"}
            </Button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
